import type {BillingGranularity, BillingInfoItem} from "../../AdminTypes";
import type {BillingMetricKey} from "../billingUtils";
import {Box, Paper, Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import {BarChartBar} from "./BarChartBar";
import {useTranslation} from "react-i18next";

type UsageBarChartProps = {
    billingItems: BillingInfoItem[];
    chartWidth: number;
    selectedMetric: BillingMetricKey;
    activeGranularity: BillingGranularity;
    maxMetricValue: number;
};

const UsageBarChart = ({
    billingItems,
    chartWidth,
    selectedMetric,
    activeGranularity,
    maxMetricValue,
}: UsageBarChartProps) => {
    const {t: translate} = useTranslation();

    return billingItems.length === 0 ? (
        <Paper
            elevation={0}
            sx={(theme) => ({
                p: {xs: 3, md: 4},
                borderRadius: 3,
                border: "1px dashed",
                borderColor: theme.palette.divider,
                bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.72 : 0.82),
            })}
        >
            <Stack spacing={1.5} alignItems="center" textAlign="center">
                <InsightsRoundedIcon sx={{fontSize: 40, color: "primary.main"}} />
                <Typography sx={{fontWeight: 800, color: "text.primary"}}>
                    {translate("admin.billing.noData.title")}
                </Typography>
                <Typography color="text.secondary" sx={{maxWidth: 520}}>
                    {translate("admin.billing.noData.description")}
                </Typography>
            </Stack>
        </Paper>
    ) : (
        <Box sx={{overflowX: "auto", pb: 0.5}}>
            <Box
                sx={{
                    minWidth: chartWidth,
                    display: "grid",
                    gridTemplateColumns: `repeat(${billingItems.length}, minmax(88px, 1fr))`,
                    gap: 1.5,
                    alignItems: "end",
                    minHeight: 320,
                }}
            >
                {billingItems.map((item) => (
                    <BarChartBar
                        item={item}
                        activeGranularity={activeGranularity}
                        maxMetricValue={maxMetricValue}
                        selectedMetric={selectedMetric}
                    />
                ))}
            </Box>
        </Box>
    );
};
export default UsageBarChart;
